import { apiRequest } from './client.js';
import type { ExpenseHead } from '../../../shared/contracts/other-expenses.js';

export interface GstSection { readonly code: string; readonly label: string }
export interface GstCapabilities { readonly create: boolean; readonly edit?: boolean; readonly effectiveDating: boolean; readonly physicalDelete: boolean }
export interface EffectiveRow { readonly effectiveFromDate: string; readonly effectiveToDate: string; readonly recordStatusCode: string }
export interface GstJurisdiction extends EffectiveRow {
  readonly jurisdictionId: string; readonly countryCode: string; readonly jurisdictionCode: string;
  readonly jurisdictionName: string; readonly jurisdictionType: string; readonly gstStateCode: string;
}
export interface GstClassification extends EffectiveRow {
  readonly classificationId: string; readonly classificationType: string; readonly classificationCode: string;
  readonly description: string; readonly chapterCode: string | null; readonly unitCode: string | null;
}
export interface GstTaxComponent extends EffectiveRow {
  readonly taxComponentId: string; readonly componentCode: string; readonly componentName: string;
  readonly supplyScopeCode: string; readonly calculationOrder: number;
}
export interface GstOption extends EffectiveRow {
  readonly gstOptionId: string; readonly optionGroupCode: string; readonly optionCode: string;
  readonly optionLabel: string; readonly displayOrder: number; readonly optionMetadata: Record<string, unknown>;
}
export interface GstRateSchedule extends EffectiveRow {
  readonly rateScheduleId: string; readonly classificationId: string; readonly taxabilityType: string;
  readonly reverseCharge: boolean; readonly notificationReference: string | null;
}
export interface GstRateComponent {
  readonly rateComponentId: string; readonly rateScheduleId: string; readonly taxComponentId: string; readonly ratePct: string;
}
export interface CompanyRow { readonly id: string; readonly companyCode: string; readonly companyName: string; readonly legalName: string; readonly panNumber: string | null }
export interface ItemRow { readonly brandingItemId: string; readonly itemCode: string; readonly itemName: string }
export interface GstRegistration extends EffectiveRow {
  readonly gstRegistrationId: string; readonly companyId: string; readonly jurisdictionId: string; readonly gstin: string;
  readonly legalName: string; readonly tradeName: string | null; readonly registrationTypeCode: string;
  readonly addressLine1: string; readonly addressLine2: string | null; readonly locality: string | null;
  readonly city: string; readonly postalCode: string;
}
export interface GstProfile extends EffectiveRow {
  readonly gstProfileId: string; readonly companyId: string; readonly gstRegistrationId: string;
  readonly placeOfSupplyRule: string; readonly filingFrequencyCode: string | null; readonly roundingModeCode: string;
  readonly roundingScale: number; readonly reverseChargeEnabled: boolean; readonly eInvoiceEnabled: boolean;
  readonly eWayBillEnabled: boolean;
}
export interface GstItemMapping extends EffectiveRow {
  /** Exactly one of the two: the branding item, or the other-expense head. */
  readonly gstItemMappingId: string; readonly brandingItemId: string | null; readonly expenseTypeId: string | null;
  readonly classificationId: string; readonly taxabilityType: string; readonly reverseChargeOverride: boolean | null;
}

/** An HSN/SAC code staged from a source document. It affects nothing until it
 *  is promoted into the catalogue. `statedRateText` is the source's own wording
 *  and may read "12% or 18%", so it is shown for judgement and never parsed. */
export interface GstClassificationImport {
  readonly id: string; readonly classificationType: string; readonly classificationCode: string;
  readonly description: string; readonly statedRateText: string; readonly sourceFileName: string;
  readonly directoryMatchStatus: string; readonly directoryDescription: string | null;
  readonly verificationStatus: string; readonly alreadyPublished: boolean;
}

export interface PlatformGstBootstrap {
  readonly asOn: string; readonly sections: readonly GstSection[]; readonly capabilities: GstCapabilities;
  readonly jurisdictions: readonly GstJurisdiction[]; readonly classifications: readonly GstClassification[];
  readonly taxComponents: readonly GstTaxComponent[]; readonly rateSchedules: readonly GstRateSchedule[];
  readonly rateComponents: readonly GstRateComponent[]; readonly options: readonly GstOption[];
  readonly classificationImports: readonly GstClassificationImport[];
}
export interface CompanyGstBootstrap {
  readonly asOn: string; readonly sections: readonly GstSection[]; readonly capabilities: GstCapabilities;
  readonly companies: readonly CompanyRow[]; readonly items: readonly ItemRow[];
  readonly jurisdictions: readonly GstJurisdiction[]; readonly classifications: readonly GstClassification[];
  readonly options: readonly GstOption[]; readonly registrations: readonly GstRegistration[];
  readonly profiles: readonly GstProfile[]; readonly itemMappings: readonly GstItemMapping[];
  readonly expenseHeads: readonly ExpenseHead[];
  readonly tdsSections: readonly TdsSection[];
}

/** A TDS section in force on the day asked for (backend common/tds-sections). A
 *  threshold the section does not have is null. */
export interface TdsSection {
  readonly sectionCode: string; readonly description: string;
  readonly rateIndividualHufPct: number; readonly rateOtherPct: number; readonly rateNoPanPct: number;
  readonly singlePaymentThreshold: number | null; readonly annualAggregateThreshold: number | null;
  readonly effectiveFromDate: string; readonly effectiveToDate: string;
}

export const getPlatformGstBootstrap = (asOn: string, token?: string | null) =>
  apiRequest<PlatformGstBootstrap>(`/v1/m17/gst/platform/bootstrap?asOn=${encodeURIComponent(asOn)}`, { token });
export const getCompanyGstBootstrap = (asOn: string, token?: string | null) =>
  apiRequest<CompanyGstBootstrap>(`/v1/m17/gst/company/bootstrap?asOn=${encodeURIComponent(asOn)}`, { token });

export const createPlatformGstRecord = <T>(resource: string, body: T, token?: string | null) =>
  apiRequest(`/v1/m17/gst/platform/${resource}`, { method: 'POST', body, token });
export const createCompanyGstRecord = <T>(resource: string, body: T, token?: string | null) =>
  apiRequest(`/v1/m17/gst/company/${resource}`, { method: 'POST', body, token });